import type { RouteDefinition } from '@solidjs/router';

export type PageInfo = { mainQuery?: readonly unknown[] };

export type DeclaredPage = { pattern: string; mainQuery: readonly unknown[] };

const join = (parent: string, child: string): string => {
  const glued = `${parent.replace(/\/$/, '')}/${child.replace(/^\//, '')}`;
  return glued.length > 1 ? glued.replace(/\/$/, '') : '/';
};

export const declaredPages = (defs: readonly RouteDefinition[], parent = '/'): DeclaredPage[] =>
  defs.flatMap((def) => {
    const paths = Array.isArray(def.path) ? def.path : [def.path ?? '/'];
    return paths.flatMap((path) => {
      const pattern = join(parent, path);
      const own = (def.info as PageInfo | undefined)?.mainQuery;
      const here = own ? [{ pattern, mainQuery: own }] : [];
      const children = def.children ? declaredPages(Array.isArray(def.children) ? def.children : [def.children], pattern) : [];
      return [...here, ...children];
    });
  });

export const matchesPattern = (pattern: string, pathname: string): boolean => {
  const want = pattern.split('/').filter(Boolean);
  const got = pathname.split('/').filter(Boolean);
  let i = 0;
  for (const seg of want) {
    if (seg.startsWith(':') && seg.endsWith('?')) {
      if (i < got.length) i++;
      continue;
    }
    if (i >= got.length) return false;
    if (!seg.startsWith(':') && seg !== got[i]) return false;
    i++;
  }
  return i === got.length;
};

export const mainQueryResolver = (defs: readonly RouteDefinition[]) => {
  const pages = declaredPages(defs).sort(
    (a, b) => (a.pattern.includes(':') ? 1 : 0) - (b.pattern.includes(':') ? 1 : 0),
  );
  return (pathname: string): readonly unknown[] | null =>
    pages.find((p) => matchesPattern(p.pattern, pathname))?.mainQuery ?? null;
};
